/**
 * L2 CheckList — a list of things to do, each with a DONE state the data owns.
 *
 * One row is one thing: a time (small, accent), a title, a meta line, and ONE
 * trailing action that reads `doneLabel` while the row is open and `undoLabel`
 * once it is done. Tapping it emits `toggled{key, done, title}` with the state
 * the user is asking for; the row never flips itself — done is a fact about the
 * data, not a mood of the widget. Every colour is a token.
 */
import { resetButton } from './ui';

/** Reading size for a row title: halfway between the body and the large step. */
const TITLE_SIZE = 'calc((var(--t-md) + var(--t-lg)) / 2)';

function rowsOf(source, data) {
  const raw = source !== undefined ? source : data;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && typeof row === 'object' && row.key != null && String(row.title || '').trim())
    .map((row) => ({
      key: String(row.key),
      title: String(row.title).trim(),
      meta: row.meta != null && String(row.meta).trim() ? String(row.meta).trim() : '',
      time: row.time != null && String(row.time).trim() ? String(row.time).trim() : '',
      done: row.done === true,
    }));
}

export function CheckList({ props = {}, data, emit }) {
  const rows = rowsOf(props.items, data);
  const doneLabel = props.doneLabel ? String(props.doneLabel) : 'Done';
  const undoLabel = props.undoLabel ? String(props.undoLabel) : 'Undo';
  const doneMeta = props.doneMeta ? String(props.doneMeta) : '';

  if (!rows.length) {
    return props.emptyLabel ? (
      <div data-checklist-empty style={{ padding: 'var(--sp-3)', fontSize: TITLE_SIZE, color: 'var(--text-2)' }}>
        {String(props.emptyLabel)}
      </div>
    ) : null;
  }

  return (
    <div data-checklist style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {rows.map((row, i) => {
        const meta = row.done && doneMeta ? doneMeta : row.meta;
        return (
          <div
            key={row.key} data-row={row.key} data-done={row.done ? 'true' : 'false'}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', minHeight: 64,
              padding: 'var(--sp-3) 0',
              borderTop: i ? '1px solid var(--line-soft)' : 'none',
            }}
          >
            {row.time ? (
              <div data-time style={{ flex: '0 0 56px', whiteSpace: 'nowrap', fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text-2)' }}>{row.time}</div>
            ) : null}
            <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* "name + dose": the dose sits at the end, so a one-line ellipsis drops the
                  part that matters. Two-line clamp instead (the Flutter kit does the same). */}
              <div style={{
                fontSize: TITLE_SIZE, fontWeight: 500, color: 'var(--text)',
                overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflowWrap: 'anywhere',
              }}>{row.title}</div>
              {meta ? (
                <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>{meta}</div>
              ) : null}
            </div>
            <button
              type="button" data-toggle={row.key} aria-pressed={row.done ? 'true' : 'false'}
              aria-label={`${row.done ? undoLabel : doneLabel}: ${row.title}`}
              onClick={emit ? () => emit('toggled', { key: row.key, done: !row.done, title: row.title }) : undefined}
              style={{
                ...resetButton,
                flex: '0 0 auto', minHeight: 40, minWidth: 76, padding: '0 var(--sp-4)',
                borderRadius: 'var(--r-pill)', fontSize: 'var(--t-sm)', fontWeight: 600,
                background: row.done ? 'var(--surface)' : 'var(--accent)',
                color: row.done ? 'var(--text)' : 'var(--accent-ink)',
                border: row.done ? '1px solid var(--line)' : '1px solid transparent',
                cursor: emit ? 'pointer' : 'default',
              }}
            >
              {row.done ? undoLabel : doneLabel}
            </button>
          </div>
        );
      })}
    </div>
  );
}
